/*                                                                                                                
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--'
*/

import { GeofenceDrawManager } from '../../map/draw';
import { VisionApplication } from '../../application';
import { PublishTopic } from '../../events';
import { VisionWindowManager } from '../../window/manager';
import { VisionWindow } from '../../window/window';
import { RockPitService, RockPitDTO } from './rockpit_service';
import { Measurement } from '../../components/measurement';

export class VisionEditPitWindow extends VisionWindow {
    private layout!: dhtmlXLayoutObject;
    private pits!: dhtmlXGridObject;
    private form!: dhtmlXForm;
    private toolbar!: dhtmlXToolbarObject;
    private drawingManager!: GeofenceDrawManager;
    private rockPitService!: RockPitService;
    map!: google.maps.Map;

    private rockPitCache!: Map<string, RockPitDTO>;

    constructor(manager: VisionWindowManager) {
        super(manager, {
            id: 'pit_window',
            title: 'Edit Pit Window',
            x: 300,
            y: 300,
            width: 1600,
            height: 600,
        });
        this.window.setText('Rock Pits');
        this.modal = false;
        this.rockPitCache = new Map<string, RockPitDTO>();

        this.buildLayout();
    }

    async buildLayout() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { DrawingManager } = (await google.maps.importLibrary('drawing')) as google.maps.DrawingLibrary;
        this.rockPitService = this.manager.app.rockPitService;

        this.layout = this.window.attachLayout('3W');
        this.layout.cells('a').hideArrow();
        this.layout.cells('b').hideArrow();

        // Device Grid
        this.layout.cells('a').setText('Pits');
        this.pits = this.layout.cells('a').attachGrid();
        this.pits.setHeader('Name', '', []);
        this.pits.setColSorting('str');
        this.pits.init();

        // Device Properties
        this.layout.cells('b').setText('Properties');
        this.toolbar = this.layout.cells('b').attachToolbar();
        this.toolbar.addButton('new', 0, 'New', 'css/imgs/new.gif', '');
        this.toolbar.addButton('save', 1, 'Save', 'css/imgs/save.gif', '');
        this.toolbar.addSeparator('sep1', 2);
        this.toolbar.addButton('delete', 3, 'Delete', 'css/imgs/cut.gif', '');

        this.layout.cells('c').setText('Map');
        this.map = this.layout.cells('c').attachMap({ center: { lat: 41.96593, lng: -91.67687 }, zoom: 8 });

        this.drawingManager = new GeofenceDrawManager(this.map, undefined);

        const form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 90,
                inputWidth: 130,
            },
            {
                name: 'id',
                type: 'input',
                label: 'ID',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'name',
                type: 'input',
                label: 'Name',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                required: false,
            },
            {
                name: 'colour',
                type: 'colorpicker',
                label: 'Color',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                required: true,
                enableCustomColors: '1',
            },
            {
                name: 'description',
                type: 'input',
                label: 'Description',
                value: '',
                rows: '3',
                inputWidth: '250',
                offsetLeft: '25',
            },
            {
                name: 'capacity',
                type: 'input',
                label: 'Capacity',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
            },
            {
                name: 'used',
                type: 'input',
                label: 'Used',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
            },
        ];
        this.form = this.layout.cells('b').attachForm({});
        this.form.loadStruct(form_data);

        this.getPits();
        this.setupEvents();
    }

    /** Handles UI events and triggers */
    private setupEvents(): void {
        this.form.attachEvent('onChange', (name: string, value: string | number, _state: boolean) => {
            if (name == 'colour') {
                const newcolor = value as string;
                if (this.drawingManager.geofence) {
                    this.drawingManager.geofence.setOptions({ fillColor: newcolor });
                }
            }
        });

        /** Fired when the user selects a row in the geofence list */
        this.pits.attachEvent('onRowSelect', (rowId, _colInd) => {
            this.populateForm(this.rockPitCache.get(rowId));
            this.displayRockpit(this.rockPitCache.get(rowId));
        });

        /** Fired when tool bar items are clicked */
        this.toolbar.attachEvent('onClick', (itemId) => {
            if (itemId == 'new') {
                if (this.drawingManager.geofence) this.drawingManager.geofence.setMap(null);
                this.drawingManager.setMode('polygon');
                this.clearForm();
            } else if (itemId == 'save') {
                this.saveRockPit();
            } else if (itemId == 'delete') {
                this.deleteRockPit();
            }
        });
    }

    /** Clears the form when adding a new geofence
     *
     */
    private clearForm(): void {
        this.form.setItemValue('id', '');
        this.form.setItemValue('name', '');
        this.form.setItemValue('description', '');
        this.form.setItemValue('colour', '');
        this.form.setItemValue('capacity', '');
        this.form.setItemValue('used', '');
    }

    private zoomToGeofence(geofence: RockPitDTO | undefined): void {
        const bounds = new google.maps.LatLngBounds();

        geofence?.geometry.forEach((item) => {
            bounds.extend(item);
        });
        this.map.fitBounds(bounds);
    }

    /** Displace a geofence on the map.
     *  Will create a new polygon based on the DTO information and set it as the active geofence.
     *  Makes it editable and dragable.
     * @param geofence
     */
    displayRockpit(pit: RockPitDTO | undefined) {
        this.drawingManager.geofence?.setMap(null);

        this.drawingManager.geofence = new google.maps.Polygon({
            paths: pit?.geometry,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: pit?.colour,
            fillOpacity: 0.35,
            editable: true,
            draggable: true,
        });
        this.drawingManager.geofence.setMap(this.map);
        this.drawingManager.setMode('select');
        this.zoomToGeofence(pit);
    }

    /** Populates the form with the information from the DTO.
     *
     * @param geofence
     */
    private populateForm(pit: RockPitDTO | undefined) {
        this.form.setItemValue('id', pit?.id.id);
        this.form.setItemValue('name', pit?.id.name);
        this.form.setItemValue('colour', pit?.colour);
        this.form.setItemValue('description', pit?.description);
        if (pit?.capacity != undefined) {
            this.form.setItemValue('capacity', Measurement.kg2ImperialTons(pit?.capacity));
        } else {
            this.form.setItemValue('capacity', '');
        }
        this.form.setItemValue('used', pit?.used);
    }

    /** Request geofence list from the server.
     *
     */
    private getPits() {
        this.pits.clearAll(false);
        const request = this.rockPitService.cachedFetchData();
        request.then((data) => {
            data.forEach((pit) => {
                this.addRockPit(pit);
            });
        });
    }

    /** Adds the geofence to the table list and adds it to the cache
     *  @param geofence [GeofenceDTO] The geofence object to add.
     */
    private addRockPit(pit: RockPitDTO): void {
        this.rockPitCache.set(pit.id.id, pit);
        this.pits.addRow(pit.id.id, pit.id.name, 0, '', '', false);
    }

    /** Update a geofence from the server.
     *  Will add it to the cache and table if it is not found.
     *  Otherwise will update the cell with name and reset the item in the cache.
     *  @param pit [RockPitDTO] The pit object to add.
     */
    private updateRockPit(pit: RockPitDTO): void {
        if (this.rockPitCache.has(pit.id.id)) {
            this.pits.cells(pit.id.id, 0).setValue(pit.id.name);
            this.rockPitCache.set(pit.id.id, pit);
        } else {
            this.addRockPit(pit);
        }
    }

    /** Send request to save the geofence based on the area drawn and data in the form.
     *
     */
    private saveRockPit(): void {
        if (this.drawingManager.geofence == undefined) {
            return;
        }

        // Collect form data.
        const path = this.drawingManager.geofence.getPath().getArray();
        path.push(path[0]);
        const id = this.form.getItemValue('id');
        const name = this.form.getItemValue('name');
        const colour = this.form.getItemValue('colour');
        const description = this.form.getItemValue('description');
        const capacity = this.form.getItemValue('capacity');
        const used = this.form.getItemValue('used');

        // Build request
        const data = {} as RockPitDTO;
        data.id = { id: id, name: name };
        data.geometry = path;
        data.colour = colour;
        data.capacity = capacity;
        data.capacity = Measurement.imperialTons2Kg(Number(capacity));
        data.used = used;
        data.description = description;

        if (data.used == undefined) {
            data.used = 0;
        }

        // Process results.
        const request = this.rockPitService.save(data);
        request.then((data) => {
            if (data?.ok) {
                VisionApplication.getInstance().pubsub.publish(PublishTopic.ROCKPITS_CHANGED);

                if (data.parsedBody) {
                    this.updateRockPit(data.parsedBody);
                    dhtmlx.message({
                        title: 'Rock Pit Saved',
                        text: 'Rock Pit ' + data.parsedBody?.id.name + ' was saved.',
                        type: 'alert-success',
                    });
                }
            }
        });
    }

    private deleteRockPit(): void {
        const id = this.form.getItemValue('id');
        const name = this.form.getItemValue('name');

        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Delete Rock Pit: ' + name,
            text: 'This will delete the rock pit and any associated events and tracking on this pit.',
            callback: (result: boolean) => {
                if (result == true) {
                    VisionApplication.getInstance()
                        .rockPitService.deletePit(id)
                        .then((data) => {
                            if (data.parsedBody) {
                                dhtmlx.message({
                                    title: 'Complete',
                                    text: 'Rockpit was deleted',
                                    type: 'alert-success',
                                });
                                this.form.setItemValue('id', '');
                                this.form.setItemValue('name', '');
                                this.getPits();
                                this.clearForm();
                            }
                        });
                }
            },
        });
    }
}
